import * as React from "react";
import cx from "classnames";
import keycode from "keycode";
import { Transition } from "react-transition-group";
import useReactRouter from "use-react-router";
import { Flipped } from "react-flip-toolkit";
import {
  getDiscoverTrendingSections,
  getDiscoverTrendingSectionContents,
  ActionCreator,
  getDiscoverTrendingSection,
} from "@vingle/wtf-sdk/actions/discover";
// components
import Trending from "./component";
import FreezeView from "@vingle/wtf-sdk/components/freezeView";
import TrendingContainerHelmet from "./helmet";
import { VingleURL } from "@vingle/wtf-sdk/url";
import { useTrackTicketReadDuration } from "@vingle/wtf-sdk/helpers/trackTicket";
import { useCancelToken, useEventListener } from "@vingle/wtf-sdk/helpers/hook";
import shouldFlipCallback from "app/discover/helpers/shouldFlipCallback";

// selector
import {
  selectedSectionContentsSelector,
  discoverTrendingSectionsSelector,
  selectedSectionSelector,
} from "@vingle/wtf-sdk/selectors/discover";
import { isBrowser } from "@vingle/wtf-sdk/helpers/envChecker";
import { useStoreState, useActions } from "app/store";

const styles = require("./trending.scss");

interface IProps {
  sectionId?: string;
  isModal?: boolean;
}

interface IHookProps extends ReturnType<typeof useProps> {}
interface IHookHandlers extends ReturnType<typeof useHandlers> {}

function useProps(props: IProps) {
  const actions = useActions({
    dispatchGetDiscoverTrendingSections: getDiscoverTrendingSections,
    dispatchGetDiscoverTrendingSection: getDiscoverTrendingSection,
    dispatchGetDiscoverTrendingSectionContents: getDiscoverTrendingSectionContents,
    dispatchSelectTrendingSection: ActionCreator.selectTrendingSection,
  });
  const router = useReactRouter<Vingle.IMatchParams>();
  const states = useStoreState(state => ({
    sections: discoverTrendingSectionsSelector(state),
    selectedSection: selectedSectionSelector(state),
    selectedSectionContents: selectedSectionContentsSelector(state),
    pageState: state.discoverPage,
  }));
  const sectionId = props.sectionId || router.match.params.sectionId || null;
  const currentSection = React.useMemo(
    () => states.sections.find(section => section.id === sectionId),
    [states.sections, sectionId],
  );
  const cancelToken = useCancelToken(sectionId);
  return {
    ...actions,
    ...router,
    ...states,
    sectionId,
    cancelToken,
    currentSection,
  };
}

function useHandlers(props: IHookProps) {
  const {
    sectionId,
    cancelToken,
    dispatchGetDiscoverTrendingSections,
    dispatchGetDiscoverTrendingSection,
    sections,
    history,
    selectedSection,
    selectedSectionContents,
    dispatchSelectTrendingSection,
    dispatchGetDiscoverTrendingSectionContents,
    currentSection,
  } = props;
  const handleLoadContentsData = React.useCallback(
    (loadSectionId: string, after?: Vingle.PagingValue) => {
      dispatchGetDiscoverTrendingSectionContents({
        sectionId: loadSectionId,
        cancelToken: cancelToken.current.token,
        after,
      });
    },
    [],
  );
  const handleFetchData = React.useCallback(() => {
    if (sectionId) {
      if (!currentSection) {
        dispatchGetDiscoverTrendingSection({
          sectionId,
          cancelToken: cancelToken.current.token,
        });
      }
      if (!sections.length) {
        dispatchGetDiscoverTrendingSections({
          cancelToken: cancelToken.current.token,
        });
      }
      if ((selectedSection && selectedSection.id) !== sectionId) {
        dispatchSelectTrendingSection({ sectionId });
      }
      if (!selectedSectionContents) {
        handleLoadContentsData(sectionId);
      }
    }
  }, [sectionId, sections]);
  const handleClose = React.useCallback(() => {
    dispatchSelectTrendingSection({ sectionId: null });
    history.goBack();
  }, []);
  const handleSelectSection = React.useCallback(
    (section: Vingle.Discover.ITrendingSection) => {
      dispatchSelectTrendingSection({
        sectionId: section.id,
      });
      history.replace(
        new VingleURL.DiscoverTrendingSection({
          sectionId: section.id,
        }).toLocation({
          modal: true,
        }),
      );
    },
    [],
  );
  return {
    handleClose,
    handleFetchData,
    handleLoadContentsData,
    handleSelectSection,
  };
}

function useEffects(props: IHookProps, handlers: IHookHandlers) {
  const { sectionId, sections, dispatchSelectTrendingSection } = props;
  const { handleFetchData, handleClose } = handlers;
  React.useEffect(() => {
    handleFetchData();
  }, [sectionId]);
  React.useEffect(() => {
    return () => {
      dispatchSelectTrendingSection({
        sectionId: null,
      });
    };
  }, []);

  if (isBrowser()) {
    useEventListener("keyup", e => {
      if (e.keyCode === keycode("esc")) {
        handleClose();
      }
    });

    // Start tractTicket with duration
    useTrackTicketReadDuration(sectionId, {
      contentType: "trending_section",
      contentId: String(sectionId),
      position: sections.findIndex(({ id }) => id === sectionId),
      referralUrl: React.useMemo(() => new VingleURL.Discover().toSchema(), []),
    });
  }
}

function TrendingContainer(props: IProps) {
  const hookProps = useProps(props);
  const hookHandlers = useHandlers(hookProps);
  useEffects(hookProps, hookHandlers);
  const {
    selectedSection,
    selectedSectionContents,
    sections,
    pageState,
    sectionId,
    currentSection,
  } = hookProps;
  const {
    handleLoadContentsData,
    handleClose,
    handleSelectSection,
  } = hookHandlers;
  const { isModal } = props;
  return (
    <>
      {sectionId && (
        <TrendingContainerHelmet
          section={currentSection}
          selectedSectionContents={selectedSectionContents}
          selectedSectionId={selectedSection && selectedSection.id}
        />
      )}
      <FreezeView isFreeze={Boolean(sectionId && isModal)}>
        {ref => (
          <Transition
            in={Boolean(sectionId)}
            unmountOnExit={true}
            timeout={350}
          >
            {status => (
              <Flipped
                flipId={`discoverSection__${sectionId}`}
                shouldFlip={shouldFlipCallback}
                opacity={false}
                translate={true}
                scale={true}
              >
                <div
                  className={cx({
                    [styles.modal]: isModal,
                    [styles.modalLeaveActive]:
                      status === "exiting" || status === "exited",
                  })}
                  ref={ref}
                >
                  {sectionId && (
                    <Trending
                      isModal={Boolean(isModal)}
                      sectionId={sectionId}
                      contents={selectedSectionContents}
                      sections={sections}
                      isTrendingSectionContentsLoading={
                        pageState.isTrendingSectionContentsLoading
                      }
                      isTrendingSectionsLoading={
                        pageState.isTrendingSectionsLoading
                      }
                      handleLoadContentsData={handleLoadContentsData}
                      onClose={handleClose}
                      onSelectSection={handleSelectSection}
                    />
                  )}
                </div>
              </Flipped>
            )}
          </Transition>
        )}
      </FreezeView>
    </>
  );
}

export default React.memo(TrendingContainer);
